import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { ExcitingWritingIdeasCatalogueCard } from 'teacher-ui-client/components/writing-legends/ewi-catalogue-card.gts'
import NoResultsMessage from 'teacher-ui-client/components/no-results-message'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector/index.gts'
import type WritingLegendsCatalogueService from 'teacher-ui-client/services/writing-legends-catalogue'
import { joinQueryParams, type QueryParams } from 'teacher-ui-client/utils/uri'
import { AllGradesOption } from '../base/select/grade-position/options'
import { cached } from '@glimmer/tracking'

export interface EWICatalogueItem {
  activity_id: string
  title: string
  thumbnail: string
  tags: string[]
}

interface Signature {
  Args: {
    textFilter?: Nullable<string>
    tagFilter?: Nullable<string>
    pageNumber: number
    perPage: number
    grade?: number
  }
}

export interface EWICatalogueData {
  data: {
    items: EWICatalogueItem[]
  }
}

export class EWICatalogueList extends Component<Signature> {
  @service writingLegendsCatalogue!: WritingLegendsCatalogueService

  fetchData = trackedFunction(this, () => {
    let url = this.writingLegendsCatalogue.buildUrlEWI()

    const queryParams: QueryParams = {
      take: 1000, // will fetch all the activities
      grade: this.args.grade !== AllGradesOption.id ? this.args.grade : undefined,
    }

    url = joinQueryParams(url, queryParams).href

    return this.writingLegendsCatalogue.fetchData<EWICatalogueData>(url)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get ewiCatalogue() {
    return this.latestData?.data.items ?? []
  }

  @cached
  get filteredEWICatalogue() {
    let filteredCatalogue = this.ewiCatalogue

    if (this.args.textFilter) {
      const textFilterLower = this.args.textFilter.toLowerCase()
      filteredCatalogue = filteredCatalogue.filter((item) => item.title.toLowerCase().includes(textFilterLower))
    }

    if (this.args.tagFilter && this.args.tagFilter !== 'All') {
      const tagFilterLower = this.args.tagFilter.toLowerCase()
      filteredCatalogue = filteredCatalogue.filter((item) =>
        item.tags.some((tag) => tag.toLowerCase().includes(tagFilterLower)),
      )
    }

    return filteredCatalogue
  }

  @cached
  get pagedEWICatalogue() {
    const startIndex = (this.args.pageNumber - 1) * this.args.perPage
    return this.filteredEWICatalogue.slice(startIndex, startIndex + this.args.perPage)
  }

  @cached
  get totalPages() {
    const totalItems = this.filteredEWICatalogue.length ?? 0
    return Math.ceil(totalItems / this.args.perPage)
  }

  <template>
    {{#if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else}}
      <div
        class="mx-4 inline-grid grid-cols-1 gap-8 md:grid-cols-2 lg:mx-0 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
      >
        {{#each this.pagedEWICatalogue as |ewi|}}
          <ExcitingWritingIdeasCatalogueCard
            @activity_id={{ewi.activity_id}}
            @title={{ewi.title}}
            @thumbnail={{ewi.thumbnail}}
            @tags={{ewi.tags}}
            @filteredTag={{@tagFilter}}
          />
        {{/each}}
      </div>

      {{#unless this.filteredEWICatalogue.length}}
        <NoResultsMessage />
      {{/unless}}

      <BasePageSelector
        class="mt-8"
        @pageCount={{this.totalPages}}
        @itemCount={{this.filteredEWICatalogue.length}}
        @itemsPerPage={{@perPage}}
      />
    {{/if}}
  </template>
}
